import { useEffect, useState } from "react";
import api, { fmtErr } from "@/lib/api";

/**
 * Daily Challenge — randomized historical NY session, 4-trade cap, AI-scored.
 * Spins up a $10K isolated session targeting a random past NY 09:30–16:00 EST window.
 * Constraint: max 4 trades. Scoring (Speed/Accuracy/Profit/R:R/Frequency = 100 pts max)
 * is wired server-side at /api/challenges/{id}/finish.
 */
export default function ChallengeModal({ onClose, onStarted, onOpenLeaderboard }) {
  const [active, setActive] = useState(null);
  const [hist, setHist] = useState([]);
  const [points, setPoints] = useState({ daily: 0, weekly: 0, monthly: 0, yearly: 0 });
  const [err, setErr] = useState("");

  useEffect(() => { reload(); }, []);
  async function reload() {
    try {
      const [a, h, ...ps] = await Promise.all([
        api.get("/challenges/active"),
        api.get("/challenges/history"),
        api.get("/challenges/points", { params: { range: "daily" } }),
        api.get("/challenges/points", { params: { range: "weekly" } }),
        api.get("/challenges/points", { params: { range: "monthly" } }),
        api.get("/challenges/points", { params: { range: "yearly" } }),
      ]);
      setActive(a.data?.id ? a.data : null);
      setHist(h.data || []);
      setPoints({
        daily: ps[0]?.data?.points || 0,
        weekly: ps[1]?.data?.points || 0,
        monthly: ps[2]?.data?.points || 0,
        yearly: ps[3]?.data?.points || 0,
      });
    } catch (e) { setErr(fmtErr(e.response?.data?.detail) || e.message); }
  }
  async function start() {
    try {
      const { data } = await api.post("/challenges/start", {});
      // Fire callback so Dashboard can auto-jump to the seed time
      if (onStarted) onStarted(data);
      onClose && onClose();
    } catch (e) { setErr(fmtErr(e.response?.data?.detail) || e.message); }
  }
  async function finish() {
    if (!active) return;
    if (!window.confirm("Finish this challenge and score it now?")) return;
    try { await api.post(`/challenges/${active.id}/finish`); await reload(); }
    catch (e) { setErr(fmtErr(e.response?.data?.detail) || e.message); }
  }

  return (
    <div data-testid="challenge-modal" className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4 font-mono">
      <div className="bg-[#0f131b] border border-slate-800 rounded-2xl max-w-3xl w-full p-6 shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-4 border-b border-slate-800 pb-3">
          <div>
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <i className="fa-solid fa-bolt text-amber-400"/> Daily Challenge
            </h2>
            <p className="text-[11px] text-slate-500">Random NY 09:30–16:00 EST · $10K isolated · max 4 trades · 100 pts/session</p>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="challenge-rank-btn"
              onClick={() => { onClose && onClose(); onOpenLeaderboard && onOpenLeaderboard("challenge"); }}
              className="px-2.5 py-1.5 bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/40 text-amber-200 rounded text-[10px] font-bold uppercase tracking-wider">
              <i className="fa-solid fa-trophy mr-1"/> Rank
            </button>
            <button data-testid="challenge-close" onClick={onClose} className="text-slate-500 hover:text-rose-300 text-xl">
              <i className="fa-solid fa-xmark"/>
            </button>
          </div>
        </div>

        {err && <div className="mb-3 text-xs text-rose-300 bg-rose-500/10 border border-rose-500/20 px-3 py-2 rounded">{err}</div>}

        {/* Independent reset counters */}
        <div className="grid grid-cols-4 gap-2 mb-4">
          <Counter label="Daily"   value={points.daily}/>
          <Counter label="Weekly"  value={points.weekly}/>
          <Counter label="Monthly" value={points.monthly}/>
          <Counter label="Yearly"  value={points.yearly}/>
        </div>

        {active ? (
          <div className="bg-slate-900/60 border border-amber-500/30 rounded-xl p-4 mb-4" data-testid="active-challenge">
            <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold mb-1">Session active</div>
            <div className="grid grid-cols-3 gap-3 text-xs">
              <Field label="Session start" value={(active.session_start_utc || "").slice(0,16).replace("T"," ")}/>
              <Field label="Capital" value={`$${active.starting_capital}`}/>
              <Field label="Trades used" value={`${(active.trade_ids || []).length}/${active.max_trades}`}/>
            </div>
            <button data-testid="challenge-finish" onClick={finish}
              className="mt-3 w-full py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold">
              Finish & Score Session
            </button>
          </div>
        ) : (
          <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-6 text-center mb-4">
            <p className="text-slate-300 text-sm mb-3">No active challenge. Spin up a fresh $10K session against a random past NY day.</p>
            <button data-testid="challenge-start" onClick={start}
              className="px-5 py-2 bg-gradient-to-r from-amber-500 to-rose-500 text-slate-900 rounded-lg text-xs font-black uppercase tracking-wider">
              <i className="fa-solid fa-play mr-1"/> Start Challenge
            </button>
            <div className="text-[10px] text-slate-600 mt-2">Chart auto-jumps to a random past NY session · max 4 trades · back-bar locked</div>
          </div>
        )}

        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">Recent sessions</div>
          {hist.length === 0 && <div className="text-slate-600 text-xs">No challenges yet.</div>}
          <div className="space-y-1">
            {hist.slice(0, 10).map(h => (
              <div key={h.id} className="flex items-center justify-between bg-slate-900/40 border border-slate-800 rounded px-3 py-2 text-xs">
                <div>
                  <span className="text-slate-400">{(h.created_at || "").slice(0,16).replace("T"," ")}</span>
                  <span className="text-slate-600 ml-2">·</span>
                  <span className={`ml-2 ${h.status === "complete" ? "text-emerald-300" : "text-amber-300"} uppercase font-bold`}>{h.status}</span>
                </div>
                <div className="text-purple-300 font-bold">{(h.score?.total ?? "—")} pts</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Counter({ label, value }) {
  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-lg p-3 text-center">
      <div className="text-[9px] text-slate-500 uppercase font-bold">{label}</div>
      <div className="text-xl font-bold text-purple-300 tabular-nums">{value}</div>
    </div>
  );
}
function Field({ label, value }) {
  return (
    <div>
      <div className="text-[9px] text-slate-500 uppercase">{label}</div>
      <div className="text-sm text-white font-bold">{value}</div>
    </div>
  );
}
